import { API_BASE_URL, motogoFetch } from './api';

export type AuthProvider = 'google' | 'microsoft';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

/**
 * Fetches the current user from <c>GET /me</c>. Returns null on 401 (unauthenticated),
 * throws on other errors.
 */
export async function fetchMe(): Promise<CurrentUser | null> {
  const res = await motogoFetch(`${API_BASE_URL}/me`);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`GET /me failed: ${res.status}`);
  const body = (await res.json()) as {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
  };
  return body;
}

/**
 * Kicks off the OAuth flow with the given provider. This is a top-level navigation,
 * not a fetch -- the backend will redirect to Google/Microsoft, then back to the PWA
 * once a session cookie is set.
 */
export function signIn(provider: AuthProvider): void {
  window.location.href = `${API_BASE_URL}/auth/login/${provider}`;
}

/**
 * Local email + password login. Returns the user on success; throws with a user-facing
 * message on failure (401 = bad credentials, validation errors = 400 with body).
 */
export async function loginLocal(email: string, password: string): Promise<CurrentUser> {
  const res = await motogoFetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (res.status === 401) {
    throw new Error('Invalid email or password.');
  }
  if (!res.ok) {
    throw new Error(await readError(res, 'Sign-in failed.'));
  }
  return (await res.json()) as CurrentUser;
}

/**
 * Local registration. Returns the newly-created user (and sets the session cookie).
 * Throws with a user-facing message on 409 (email taken) or 400 (validation).
 */
export async function register(email: string, password: string, name: string): Promise<CurrentUser> {
  const res = await motogoFetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });
  if (res.status === 409) {
    throw new Error('An account with this email already exists.');
  }
  if (!res.ok) {
    throw new Error(await readError(res, 'Registration failed.'));
  }
  return (await res.json()) as CurrentUser;
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; title?: string };
    return body.error ?? body.title ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Tears down the current session server-side, then reloads the PWA so the auth
 * gate re-evaluates and shows the login page.
 */
export async function signOut(): Promise<void> {
  await motogoFetch(`${API_BASE_URL}/auth/logout`, { method: 'POST' });
  window.location.reload();
}
